<dx-popup
  [visible]="visible"
  (onHiding)="onHiding()"
  [fullScreen]="isMobile"
  [width]="isMobile ? '100vw' : 560"
  [height]="isMobile ? '100%' : 'auto'"
  [maxHeight]="isMobile ? '100%' : '90vh'"
  [showTitle]="true"
  [title]="title"
  [showCloseButton]="true"
  [dragEnabled]="false"
  [wrapperAttr]="{ class: 'license-wizard-wrapper' }"
>
  <dx-scroll-view>
    <div class="lw" data-testid="license-wizard">
      <!-- License type switch (DEA only where the tenant uses it) -->
      <div class="lw__types" *ngIf="allowDea" role="tablist">
        <button type="button" role="tab" class="lw__type" [class.lw__type--on]="licenseType === 'state_license'"
          [attr.aria-selected]="licenseType === 'state_license'"
          (click)="setLicenseType('state_license')" data-testid="license-wizard-type-state">State license</button>
        <button type="button" role="tab" class="lw__type" [class.lw__type--on]="licenseType === 'dea_license'"
          [attr.aria-selected]="licenseType === 'dea_license'"
          (click)="setLicenseType('dea_license')" data-testid="license-wizard-type-dea">DEA registration</button>
      </div>

      <!-- Status: what is on file now -->
      <section class="lw__status" data-testid="license-wizard-status">
        <div class="lw__status-row">
          <span class="lw__muted">On file
            <span class="lw__health" [ngClass]="'lw__health--' + health" data-testid="license-wizard-health">
              <ng-container [ngSwitch]="health">
                <ng-container *ngSwitchCase="'active'">Active</ng-container>
                <ng-container *ngSwitchCase="'expiring'">Expires in {{ daysLeft }} {{ daysLeft === 1 ? 'day' : 'days' }}</ng-container>
                <ng-container *ngSwitchCase="'expired'">Expired</ng-container>
                <ng-container *ngSwitchDefault>Missing</ng-container>
              </ng-container>
            </span>
          </span>
          <span>
            <ng-container *ngIf="onFileNumber; else noLicense">
              <strong>{{ onFileNumber }}</strong>
              <ng-container *ngIf="onFileExpiry"> · expires {{ onFileExpiry | date: 'mediumDate' }}</ng-container>
              <button *ngIf="onFileDocument?.url" type="button" class="lw__file-btn lw__onfile-view" (click)="openViewer(onFileDocument?.url, onFileDocument?.uri)"
                data-testid="license-wizard-view-file"><i class="dx-icon-doc" aria-hidden="true"></i> View file</button>
            </ng-container>
            <ng-template #noLicense>No {{ label }} on file yet</ng-template>
          </span>
        </div>
        <!-- Hidden beside an outcome: its own Submit is for the upload just made. -->
        <div class="lw__pending" *ngIf="pending && step.kind !== 'outcome' && step.kind !== 'submitted'" data-testid="license-wizard-pending">
          <p class="lw__pending-msg" *ngIf="pending.submitted; else notSubmitted">
            An upload from {{ uploadTime(pending.uploaded_at) }} is with our team for review. {{ waitCopy }}</p>
          <ng-template #notSubmitted>
            <p class="lw__pending-msg">An upload from {{ uploadTime(pending.uploaded_at) }} couldn't be automatically verified and hasn't been sent for review.</p>
          </ng-template>
          <!-- Actions on the left, the file (reference, not an action) on the right. -->
          <div class="lw__pending-row">
            <span *ngIf="!pending.submitted">
              <button type="button" class="lw__link" (click)="submitPendingFromHeader()"
                data-testid="license-wizard-submit-pending">Submit for review</button>
              <span class="lw__sep" aria-hidden="true">·</span>
              <button type="button" class="lw__link" (click)="withdrawPendingFromHeader()"
                data-testid="license-wizard-withdraw-pending">Withdraw</button>
            </span>
            <button *ngIf="pending.url" type="button" class="lw__file-btn lw__pending-view" (click)="openViewer(pending.url, pending.file_name)"
              data-testid="license-wizard-pending-file"><i class="dx-icon-doc" aria-hidden="true"></i> View file</button>
          </div>
        </div>
      </section>

      <!-- Step 1: what are you doing? -->
      <section *ngIf="step.kind === 'intent'" class="lw__step" data-testid="license-wizard-intent">
        <h3 class="lw__h">What would you like to do?</h3>
        <button type="button" class="lw__choice" (click)="chooseIntent('renew')" data-testid="license-wizard-intent-renew">
          <strong>Upload a renewed copy</strong>
          <span>Same {{ label }} number ({{ onFileNumber }}), new expiration date.</span>
        </button>
        <!-- DEA numbers don't change in practice: DEA offers renewal only. -->
        <button *ngIf="licenseType === 'state_license'" type="button" class="lw__choice" [class.lw__choice--on]="intent === 'change'"
          [attr.aria-pressed]="intent === 'change'"
          (click)="chooseIntent('change')" data-testid="license-wizard-intent-change">
          <strong>Change to a different {{ label }}</strong>
          <span>The facility is moving to a new {{ label }} number.</span>
        </button>
        <div *ngIf="intent === 'change'" class="lw__change">
          <dx-text-box [(value)]="newNumber" [label]="'New ' + label + ' number'" labelMode="floating"
            [inputAttr]="{ 'data-testid': 'license-wizard-new-number', autocapitalize: 'characters' }"></dx-text-box>
          <p class="lw__error" *ngIf="numberError">{{ numberError }}</p>
          <p class="lw__muted" *ngIf="changeIsReviewed">Changes are reviewed by our team before they apply.</p>
          <dx-button text="Continue" type="default" [width]="'100%'" (onClick)="continueWithNewNumber()"
            [elementAttr]="{ 'data-testid': 'license-wizard-continue' }"></dx-button>
        </div>
      </section>

      <!-- Step 2: what is accepted, then upload -->
      <section *ngIf="step.kind === 'guidance'" class="lw__step" data-testid="license-wizard-guidance">
        <h3 class="lw__h">{{ intent === 'change' ? 'Upload the new ' + label : 'Upload your ' + label }}</h3>
        <div class="lw__reuse" *ngIf="reusable" data-testid="license-wizard-reusable">
          <p>Your company already has a verified copy of license <strong>{{ reusable.number }}</strong>
            (expires {{ reusable.expiry | date: 'mediumDate' }}). You can use it for this facility instead of uploading.</p>
          <dx-button text="Use the license already on file" type="default" [width]="'100%'" (onClick)="useReusable()"
            [elementAttr]="{ 'data-testid': 'license-wizard-use-existing' }"></dx-button>
          <p class="lw__muted">Or upload a newer copy below.</p>
        </div>
        <ul class="lw__rules">
          <li *ngFor="let line of guidanceLines">{{ line }}</li>
        </ul>
        <div class="lw__pick">
          <dx-button text="Take a photo" icon="photo" type="default" [width]="'100%'" (onClick)="pickPhoto()"
            [elementAttr]="{ 'data-testid': 'license-wizard-take-photo' }"></dx-button>
          <dx-button text="Choose a file" icon="doc" stylingMode="outlined" [width]="'100%'" (onClick)="pickFile()"
            [elementAttr]="{ 'data-testid': 'license-wizard-choose-file' }"></dx-button>
        </div>
        <p class="lw__muted">Files can be PDF, JPEG or PNG, up to 20 MB.</p>
        <button *ngIf="hasLicenseOnFile && licenseType === 'state_license'" type="button" class="lw__link" (click)="step = { kind: 'intent' }">Back</button>
      </section>

      <!-- Step 3: checking -->
      <section *ngIf="step.kind === 'checking'" class="lw__step lw__checking" data-testid="license-wizard-checking" aria-live="polite">
        <div class="lw__spinner" aria-hidden="true"></div>
        <p>{{ checkingStage }}</p>
        <p class="lw__muted">This usually takes under a minute. Please keep this window open.</p>
      </section>

      <!-- Step 4: outcome -->
      <section *ngIf="step.kind === 'outcome'" class="lw__step" data-testid="license-wizard-outcome"
        [attr.data-outcome]="step.outcome.code" aria-live="polite">
        <div class="lw__banner" [ngClass]="'lw__banner--' + step.copy.tone">
          <h3 class="lw__h">{{ step.copy.title }}</h3>
          <p *ngFor="let line of step.copy.body">{{ line }}</p>
          <button *ngIf="lastUploadUrl" type="button" class="lw__file-btn lw__file-btn--block" (click)="openViewer(lastUploadUrl, 'The file you uploaded')"
            data-testid="license-wizard-view-upload">View the file you uploaded</button>
        </div>
        <details *ngIf="step.copy.boardHint" class="lw__hint" data-testid="license-wizard-board-hint">
          <summary>Can't get a copy of your license?</summary>
          <p>Upload the record from your state's license verification site instead:</p>
          <ol class="lw__rules">
            <li *ngFor="let line of boardSheetSteps">{{ line }}</li>
          </ol>
        </details>
        <div class="lw__actions" *ngIf="step.outcome.next_actions.length">
          <dx-button *ngFor="let action of step.outcome.next_actions; let first = first"
            [text]="actionLabel[action]"
            [type]="first ? 'default' : 'normal'"
            [stylingMode]="first ? 'contained' : 'outlined'"
            [width]="'100%'" [disabled]="busy"
            (onClick)="runAction(action)"
            [elementAttr]="{ 'data-testid': 'license-wizard-action-' + action }"></dx-button>
        </div>
        <p class="lw__muted" *ngIf="step.outcome.next_actions.includes('submit_review')">
          If you close this window, this upload won't be sent for review. You can still send it, or withdraw it, from the Facilities page. {{ waitCopy }}
        </p>
        <dx-button *ngIf="!step.outcome.next_actions.length" text="Done" type="default" [width]="'100%'" (onClick)="close()"
          [elementAttr]="{ 'data-testid': 'license-wizard-done' }"></dx-button>
      </section>

      <!-- Submitted for review -->
      <section *ngIf="step.kind === 'submitted'" class="lw__step" data-testid="license-wizard-submitted">
        <div class="lw__banner lw__banner--info">
          <h3 class="lw__h">Sent for review</h3>
          <p>Your upload has been received and is under review. {{ waitCopy }}</p>
          <p>Your current license on file stays active in the meantime.</p>
        </div>
        <dx-button text="Done" type="default" [width]="'100%'" (onClick)="close()"
          [elementAttr]="{ 'data-testid': 'license-wizard-done' }"></dx-button>
      </section>

      <!-- Error -->
      <section *ngIf="step.kind === 'error'" class="lw__step" data-testid="license-wizard-error">
        <div class="lw__banner lw__banner--warning"><p>{{ step.message }}</p></div>
        <dx-button text="Try again" type="default" [width]="'100%'" (onClick)="step = { kind: 'guidance' }"></dx-button>
      </section>

      <input #photoInput type="file" accept="image/jpeg,image/png" capture="environment" hidden (change)="onFileSelected($event)"
        data-testid="license-wizard-photo-input" />
      <input #fileInput type="file" accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf" hidden
        (change)="onFileSelected($event)" data-testid="license-wizard-file-input" />
    </div>
  </dx-scroll-view>
</dx-popup>

<app-file-viewer [url]="viewerUrl" [name]="viewerName" [(visible)]="viewerVisible"></app-file-viewer>
